import { useId, type ReactNode } from 'react';

/**
 * The game's own icons: chunky glossy shapes in the logo's style (a light-to-saturated gradient, a
 * darker outline of the same hue, a white gloss). Drawn, not emoji, so they look the same on every
 * phone; emoji are drawn by the OS and differed between Android, iPhone and Windows.
 *
 * Gradients are declared per icon (ids from useId), so an icon works anywhere, prerendered pages too.
 * Straight horizontal or vertical strokes use plain colors: a gradient on a zero-height box draws nothing.
 */

/** [top, bottom, outline] */
const TONES = {
  gold: ['#fff1a8', '#ffb420', '#a8650a'],
  amber: ['#ffc56b', '#d9801c', '#87470b'],
  yellow: ['#fff7a0', '#ffd21f', '#a87c00'],
  orange: ['#ffcf8a', '#ff7a2f', '#a83e0b'],
  red: ['#ffa3b1', '#ff3b5c', '#9c1430'],
  pink: ['#ffc6e0', '#ff4f9a', '#a81c5c'],
  purple: ['#dcb6ff', '#8b4dff', '#4f22a6'],
  ghost: ['#ffffff', '#c9c6ff', '#6f69c9'],
  blue: ['#a8d8ff', '#2f7dff', '#173f9e'],
  sky: ['#dff2ff', '#6cb8ff', '#2a6cc0'],
  cyan: ['#c4f8ff', '#22c3e6', '#0b6f8a'],
  green: ['#a6ffd4', '#18c582', '#0a6e48'],
  lime: ['#e6ff9e', '#7ed321', '#3f7a0c'],
  silver: ['#ffffff', '#b7c1d6', '#5f6b88'],
  bronze: ['#ffd9b5', '#d98745', '#7f4419'],
  slate: ['#c3cdf0', '#6f7cab', '#353f66'],
  paper: ['#ffffff', '#dce3f2', '#7f8bab'],
  cream: ['#fff6e2', '#ecc68c', '#966326'],
  cherry: ['#ff8a9a', '#d1103a', '#6e0820'],
  grape: ['#cfa4ff', '#7034d9', '#3a1382'],
  peach: ['#ffdcc4', '#ff8a6b', '#a3402a'],
  dough: ['#ffe2b0', '#d9964c', '#83501d'],
  cheese: ['#fff6b0', '#ffc532', '#a07106'],
  // Food: full, saturated color (a light top made the fruit look pastel and dull on the dark arena).
  fRed: ['#ff5a6e', '#e3122f', '#7d0a1d'],
  fCherry: ['#ff4f6a', '#c40a2e', '#62061a'],
  fOrange: ['#ffa53a', '#ff6a00', '#9a3a00'],
  fYellow: ['#ffe53d', '#ffbf00', '#946a00'],
  fLime: ['#c4f23a', '#58bd12', '#2f6a06'],
  fGreen: ['#3ee891', '#0aa860', '#065c35'],
  fGrape: ['#a96bff', '#6418e0', '#330b7c'],
  fPink: ['#ff6aae', '#ee1f7c', '#8f0f49'],
  fCyan: ['#5ae4ff', '#00acd6', '#055d75'],
  fPeach: ['#ffa27c', '#ff5f3d', '#952c18'],
  fDough: ['#f5b866', '#cc7a2a', '#73400f'],
  fCheese: ['#ffde3a', '#ffae00', '#8c5e00'],
  fSky: ['#6fc0ff', '#1f7cff', '#163f9e'],
} as const;
type Tone = keyof typeof TONES;

const INK = '#111a33';

interface Kit {
  /** Gradient fill of a tone. */
  f: (t: Tone) => string;
  /** Outline color of a tone. */
  e: (t: Tone) => string;
  /** A unique id (clip paths). */
  uid: (name: string) => string;
}

/** Outlined shape: fill with the tone's gradient, stroke with its outline color. */
const shape = (k: Kit, t: Tone, d: string, sw = 1.5) => <path d={d} fill={k.f(t)} stroke={k.e(t)} strokeWidth={sw} strokeLinejoin="round" />;
const gloss = (d: string, w = 1.8, o = 0.75) => <path d={d} fill="none" stroke="#fff" strokeWidth={w} strokeLinecap="round" opacity={o} />;

const medal = (label: ReactNode, t: Tone) => (k: Kit) => (
  <>
    <path d="M8.5 2.5h5.5l3.5 9.5h-5.5z" fill="#ff4d6d" stroke="#9c1430" strokeWidth={1.2} strokeLinejoin="round" />
    <path d="M23.5 2.5H18l-3.5 9.5H20z" fill="#2f7dff" stroke="#173f9e" strokeWidth={1.2} strokeLinejoin="round" />
    <circle cx={16} cy={20} r={8.6} fill={k.f(t)} stroke={k.e(t)} strokeWidth={1.5} />
    <circle cx={16} cy={20} r={6} fill="none" stroke={k.e(t)} strokeWidth={1} opacity={0.45} />
    {label}
    {gloss('M10.6 17.4a6 6 0 0 1 3-3.2', 1.6)}
  </>
);
const medalNumber = (n: number, t: Tone) =>
  medal(
    <text x={16} y={23.4} textAnchor="middle" fontSize={9.5} fontWeight={850} fontFamily="'Space Grotesk', system-ui, sans-serif" fill={TONES[t][2]}>
      {n}
    </text>,
    t,
  );

const ICONS: Record<string, (k: Kit) => ReactNode> = {
  coin: (k) => (
    <>
      <circle cx={16} cy={16} r={12.5} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.5} />
      <circle cx={16} cy={16} r={8.8} fill="none" stroke={k.e('gold')} strokeWidth={1.3} opacity={0.5} />
      <path d="M16 10.4l1.7 3.9 3.9 1.7-3.9 1.7-1.7 3.9-1.7-3.9-3.9-1.7 3.9-1.7z" fill="#fff8d8" />
      {gloss('M7.9 13a8.6 8.6 0 0 1 5.2-5.3', 2)}
    </>
  ),
  gem: (k) => (
    <>
      {shape(k, 'cyan', 'M9 6h14l5 7-12 14L4 13z')}
      <path d="M4 13h24M12.4 6 10 13l6 14 6-14-2.4-7" fill="none" stroke={k.e('cyan')} strokeWidth={1} opacity={0.55} strokeLinejoin="round" />
      <path d="M9.7 7.6h3.6L11.4 12H6.7z" fill="#fff" opacity={0.6} />
    </>
  ),
  trophy: (k) => (
    <>
      <path d="M9.5 8H6.2c0 4 1.6 6.1 4.7 6.6M22.5 8h3.3c0 4-1.6 6.1-4.7 6.6" fill="none" stroke={k.e('gold')} strokeWidth={2.2} strokeLinecap="round" />
      {shape(k, 'gold', 'M9 5h14v6.5a7 7 0 0 1-14 0z')}
      {shape(k, 'gold', 'M14 18.2h4l.9 4.3h-5.8z')}
      {shape(k, 'amber', 'M9.5 24a1.6 1.6 0 0 1 1.6-1.6h9.8A1.6 1.6 0 0 1 22.5 24v3h-13z')}
      {gloss('M11.6 7.2v4.3a4.6 4.6 0 0 0 1.8 3.7')}
    </>
  ),
  crown: (k) => (
    <>
      {shape(k, 'gold', 'M4.5 11.5l6 4.6L16 7.6l5.5 8.5 6-4.6-2.6 13.4H7.1z')}
      <path d="M7.6 21.6h16.8" stroke={k.e('gold')} strokeWidth={1.2} opacity={0.55} />
      <circle cx={16} cy={18.6} r={1.9} fill="#ff4d6d" stroke="#9c1430" strokeWidth={0.9} />
      <circle cx={4.5} cy={11.5} r={1.9} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.1} />
      <circle cx={16} cy={7.4} r={1.9} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.1} />
      <circle cx={27.5} cy={11.5} r={1.9} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.1} />
      {gloss('M9.4 19.2l-.9-3.6', 1.6)}
    </>
  ),
  medal1: medalNumber(1, 'gold'),
  medal2: medalNumber(2, 'silver'),
  medal3: medalNumber(3, 'bronze'),
  medalStar: medal(<path d="M16 15.6l1.4 2.9 3.2.5-2.3 2.2.5 3.1-2.8-1.5-2.8 1.5.5-3.1-2.3-2.2 3.2-.5z" fill="#fff8d8" stroke={TONES.gold[2]} strokeWidth={0.8} strokeLinejoin="round" />, 'gold'),
  globe: (k) => (
    <>
      <defs>
        <clipPath id={k.uid('g')}>
          <circle cx={16} cy={16} r={11.6} />
        </clipPath>
      </defs>
      <circle cx={16} cy={16} r={12.4} fill={k.f('blue')} stroke={k.e('blue')} strokeWidth={1.5} />
      <g clipPath={`url(#${k.uid('g')})`}>
        <path d="M5.6 9.4c2.6-.9 5 .4 5.2 2.4.2 2.2-2.6 2.4-2.4 4.6.2 2.4 3.4 2.4 3.2 5.2-.2 2.4-2.8 3.4-5.2 2.4z" fill={k.f('green')} stroke={k.e('green')} strokeWidth={1} />
        <path d="M18.8 5.6c2.6.2 6.8 2 8 4.6.6 1.4-1.4 2.6-3 3-1.6.4-2.4 2.4-1 3.8 1.4 1.4 3.6 1.4 4.8 2.8l-.6 4c-2-.4-2.4-2.6-4.2-3.2-1.8-.6-3.8.4-4.4-1.6-.6-2 1.4-3 1.4-5s-2-2.8-1.6-4.8c.2-1.4.4-2.6.6-3.6z" fill={k.f('green')} stroke={k.e('green')} strokeWidth={1} />
      </g>
      {gloss('M7.6 11.4a9.6 9.6 0 0 1 5-5', 1.8, 0.7)}
    </>
  ),
  snake: (k) => (
    <>
      <path d="M6.5 26c3.4 0 10-.6 10-4.6s-7.6-4.4-7.6-8.6S12.4 7.4 16.6 7.4" fill="none" stroke={k.e('green')} strokeWidth={7.2} strokeLinecap="round" />
      <path d="M6.5 26c3.4 0 10-.6 10-4.6s-7.6-4.4-7.6-8.6S12.4 7.4 16.6 7.4" fill="none" stroke={k.f('green')} strokeWidth={4.4} strokeLinecap="round" />
      <ellipse cx={21.4} cy={7.6} rx={5.6} ry={4.6} fill={k.f('green')} stroke={k.e('green')} strokeWidth={1.5} />
      <circle cx={22.8} cy={6.2} r={1.7} fill="#fff" />
      <circle cx={23.3} cy={6.3} r={0.9} fill={INK} />
      <path d="M26.9 8.6l2.2.6m0 0 .9-1.1m-.9 1.1.6 1.3" stroke="#ff4d6d" strokeWidth={1.1} strokeLinecap="round" fill="none" />
      {gloss('M18.2 5.6a3.6 3.6 0 0 1 2.4-1.2', 1.4)}
    </>
  ),
  bolt: (k) => (
    <>
      {shape(k, 'yellow', 'M18.6 3 7 18h7.6l-2.2 11L25 13h-7.6z')}
      {gloss('M16.2 7 11.4 13.6', 1.6)}
    </>
  ),
  flame: (k) => (
    <>
      {shape(k, 'orange', 'M16 3c1 4.6 6 6.8 7.6 11.4C25.6 20.4 21.6 29 16 29S6.4 24.6 7.4 18.6c.6-3.4 3-4.6 3.6-7.6 1.6 1.4 2.4 3 2.4 5C15.6 12.6 15 7.6 16 3z')}
      <path d="M16 15c2.2 2 4.4 4.2 4.4 7.2a4.4 4.4 0 0 1-8.8 0c0-2.2 1.4-3.4 2.2-4.8.6.8 1 1.6 1 2.6.8-1.4 1.2-3 1.2-5z" fill={k.f('yellow')} />
    </>
  ),
  burst: (k) => (
    <>
      {shape(k, 'orange', 'M16 2.5l2.6 6.4 6.2-3-2.2 6.6 6.9 1.4-5.8 4 4.4 5.4-6.8-.4.2 6.9-5.5-4.2-5.5 4.2.2-6.9-6.8.4 4.4-5.4-5.8-4 6.9-1.4-2.2-6.6 6.2 3z')}
      <path d="M16 10.5l1.6 3.6 3.6.5-2.6 2.6.6 3.6-3.2-1.7-3.2 1.7.6-3.6-2.6-2.6 3.6-.5z" fill={k.f('yellow')} />
    </>
  ),
  swords: (k) => (
    <>
      {shape(k, 'silver', 'M27.5 4.5 24.4 4 12.6 15.8l3.1 3.1L27.5 7.1z', 1.3)}
      <path d="M10.4 15.4l5.6 5.6" stroke={k.e('gold')} strokeWidth={3.4} strokeLinecap="round" />
      <path d="M10.4 15.4l5.6 5.6" stroke={k.f('gold')} strokeWidth={1.8} strokeLinecap="round" />
      <path d="M12.2 20.2 7.6 24.8" stroke="#6b3d1f" strokeWidth={3} strokeLinecap="round" />
      <circle cx={6.6} cy={25.8} r={1.9} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1} />
      {shape(k, 'silver', 'M4.5 4.5 7.6 4l11.8 11.8-3.1 3.1L4.5 7.1z', 1.3)}
      <path d="M21.6 15.4 16 21" stroke={k.e('gold')} strokeWidth={3.4} strokeLinecap="round" />
      <path d="M21.6 15.4 16 21" stroke={k.f('gold')} strokeWidth={1.8} strokeLinecap="round" />
      <path d="M19.8 20.2l4.6 4.6" stroke="#6b3d1f" strokeWidth={3} strokeLinecap="round" />
      <circle cx={25.4} cy={25.8} r={1.9} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1} />
    </>
  ),
  users: (k) => (
    <>
      <circle cx={21.5} cy={10} r={4.2} fill={k.f('blue')} stroke={k.e('blue')} strokeWidth={1.4} />
      {shape(k, 'blue', 'M14.5 26c0-5 3-8.2 7-8.2s7 3.2 7 8.2z', 1.4)}
      <circle cx={11.5} cy={11.2} r={4.8} fill={k.f('cyan')} stroke={k.e('cyan')} strokeWidth={1.5} />
      {shape(k, 'cyan', 'M3 27.5c0-5.6 3.6-9 8.5-9s8.5 3.4 8.5 9z')}
      {gloss('M8.8 9.6a2.8 2.8 0 0 1 1.8-1.4', 1.4)}
    </>
  ),
  spiral: (k) => (
    <>
      <circle cx={16} cy={16} r={12.4} fill={k.f('purple')} stroke={k.e('purple')} strokeWidth={1.5} />
      <path d="M16 16.4c0-1.1 1.7-1.5 2.5-.6 1.4 1.4.2 3.9-1.9 4.1-3.3.4-5.2-3.1-3.9-6 1.7-3.9 7.1-4.6 9.6-1.3 2.4 3.2 1 8.2-3 9.6" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" opacity={0.92} />
    </>
  ),
  gamepad: (k) => (
    <>
      {shape(k, 'purple', 'M9.6 9h12.8c3.6 0 5.7 3 6.1 7.4.4 4.6-.6 8.6-3.4 8.6-2.4 0-3.4-3.4-5.6-3.4h-7c-2.2 0-3.2 3.4-5.6 3.4-2.8 0-3.8-4-3.4-8.6C3.9 12 6 9 9.6 9z')}
      <path d="M10 13.4v5.2M7.4 16h5.2" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" />
      <circle cx={21.6} cy={14.2} r={1.7} fill="#34f5a0" stroke={INK} strokeWidth={0.6} />
      <circle cx={24.6} cy={17.2} r={1.7} fill="#ff4d6d" stroke={INK} strokeWidth={0.6} />
      {gloss('M8.4 11.2h4', 1.4)}
    </>
  ),
  key: (k) => (
    <>
      <path d="M14.4 16.4 25.8 27.8" stroke={k.e('gold')} strokeWidth={4.8} strokeLinecap="round" />
      <path d="M14.4 16.4 25.8 27.8" stroke={k.f('gold')} strokeWidth={2.4} strokeLinecap="round" />
      <path d="M21 23.2l2.6-2.6M23.8 26l2.4-2.4" stroke={k.e('gold')} strokeWidth={2.4} strokeLinecap="round" />
      <circle cx={10.5} cy={11.5} r={7} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.5} />
      <circle cx={10.5} cy={11.5} r={2.4} fill={INK} opacity={0.85} />
      {gloss('M5.8 10a5 5 0 0 1 3-3.6', 1.6)}
    </>
  ),
  robot: (k) => (
    <>
      <path d="M16 4.5v3.8" stroke={TONES.slate[2]} strokeWidth={1.8} />
      <circle cx={16} cy={4} r={2} fill="#ff4d6d" stroke="#9c1430" strokeWidth={0.8} />
      {shape(k, 'slate', 'M3.5 14a1 1 0 0 1 1-1h2v7h-2a1 1 0 0 1-1-1zM28.5 14a1 1 0 0 0-1-1h-2v7h2a1 1 0 0 0 1-1z', 1.2)}
      {shape(k, 'slate', 'M11 8h10a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5v-8a5 5 0 0 1 5-5z')}
      <rect x={9} y={12} width={14} height={7.4} rx={3.7} fill={INK} />
      <circle cx={12.8} cy={15.7} r={1.9} fill="#34f5a0" />
      <circle cx={19.2} cy={15.7} r={1.9} fill="#34f5a0" />
      <path d="M13 22.6h6" stroke={INK} strokeWidth={1.4} strokeLinecap="round" opacity={0.6} />
      {gloss('M8.4 11.6a3 3 0 0 1 2.4-2', 1.4)}
    </>
  ),
  palette: (k) => (
    <>
      {shape(k, 'cream', 'M16 4C8.8 4 4 9.2 4 15.4 4 22 9 27.5 15 27.5c2.2 0 3-1.4 2.4-3-.8-2 .4-3.6 2.6-3.6h3c3.4 0 5-2.4 5-5.6C28 8.6 22.6 4 16 4z')}
      <circle cx={10.4} cy={14.4} r={2.3} fill="#ff4d6d" stroke={INK} strokeWidth={0.5} />
      <circle cx={14.4} cy={9.2} r={2.3} fill="#ffd166" stroke={INK} strokeWidth={0.5} />
      <circle cx={20.4} cy={9.4} r={2.3} fill="#34f5a0" stroke={INK} strokeWidth={0.5} />
      <circle cx={23.6} cy={14.8} r={2.3} fill="#2f7dff" stroke={INK} strokeWidth={0.5} />
      <circle cx={11.6} cy={21} r={2} fill="#8b4dff" stroke={INK} strokeWidth={0.5} />
    </>
  ),
  bag: (k) => (
    <>
      <path d="M11 12.4V9.6a5 5 0 0 1 10 0v2.8" fill="none" stroke={k.e('blue')} strokeWidth={2.2} strokeLinecap="round" />
      {shape(k, 'blue', 'M6 11.5h20l-1.5 14.8a2.2 2.2 0 0 1-2.2 2H9.7a2.2 2.2 0 0 1-2.2-2z')}
      <circle cx={16} cy={19.6} r={3.6} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.1} />
      <path d="M16 17.6l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6z" fill="#fff8d8" />
      {gloss('M8.6 14.4l.6 8', 1.6)}
    </>
  ),
  castle: (k) => (
    <>
      <path d="M16 11.5V3.6" stroke={TONES.slate[2]} strokeWidth={1.5} />
      <path d="M16 3.6h6l-1.6 2 1.6 2h-6z" fill="#ff4d6d" stroke="#9c1430" strokeWidth={0.8} strokeLinejoin="round" />
      {shape(k, 'slate', 'M4 28V11.5h4v3h3v-3h4v3h2v-3h4v3h3v-3h4V28z')}
      <path d="M13 28v-4.6a3 3 0 0 1 6 0V28z" fill={INK} />
      <rect x={7.4} y={18} width={2.6} height={3.4} rx={1.2} fill={INK} opacity={0.8} />
      <rect x={22} y={18} width={2.6} height={3.4} rx={1.2} fill={INK} opacity={0.8} />
    </>
  ),
  tv: (k) => (
    <>
      <path d="M11.6 3.6 16 8l4.4-4.4" fill="none" stroke={TONES.slate[2]} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
      {shape(k, 'slate', 'M7.5 8h17a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4h-17a4 4 0 0 1-4-4V12a4 4 0 0 1 4-4z')}
      <rect x={6.6} y={11} width={18.8} height={12} rx={2.4} fill={INK} />
      <path d="M14 13.8v6.4l5.4-3.2z" fill="#ff4d6d" />
    </>
  ),
  cloud: (k) => (
    <>
      {shape(k, 'sky', 'M9.6 25.5h14a5.6 5.6 0 0 0 .9-11.1 8.2 8.2 0 0 0-15.6-2A6.6 6.6 0 0 0 9.6 25.5z')}
      <path d="M16 22.4v-7.8M12.7 17.8 16 14.5l3.3 3.3" fill="none" stroke={TONES.sky[2]} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
      {gloss('M10.6 12.8a6 6 0 0 1 4-3.4', 1.6)}
    </>
  ),
  lock: (k) => (
    <>
      <path d="M10.6 14.2v-3.6a5.4 5.4 0 0 1 10.8 0v3.6" fill="none" stroke={k.e('silver')} strokeWidth={4.2} />
      <path d="M10.6 14.2v-3.6a5.4 5.4 0 0 1 10.8 0v3.6" fill="none" stroke={k.f('silver')} strokeWidth={2} />
      {shape(k, 'gold', 'M10 14h12a3.4 3.4 0 0 1 3.4 3.4v7.2A3.4 3.4 0 0 1 22 28H10a3.4 3.4 0 0 1-3.4-3.4v-7.2A3.4 3.4 0 0 1 10 14z')}
      <circle cx={16} cy={19.8} r={2.1} fill={INK} />
      <rect x={15.2} y={20.4} width={1.6} height={4} rx={0.8} fill={INK} />
      {gloss('M9.2 17.2v5', 1.5)}
    </>
  ),
  unlock: (k) => (
    <>
      <path d="M10.6 14.2V9.8a5.4 5.4 0 0 1 10.5-1.8" fill="none" stroke={k.e('silver')} strokeWidth={4.2} />
      <path d="M10.6 14.2V9.8a5.4 5.4 0 0 1 10.5-1.8" fill="none" stroke={k.f('silver')} strokeWidth={2} />
      {shape(k, 'green', 'M10 14h12a3.4 3.4 0 0 1 3.4 3.4v7.2A3.4 3.4 0 0 1 22 28H10a3.4 3.4 0 0 1-3.4-3.4v-7.2A3.4 3.4 0 0 1 10 14z')}
      <circle cx={16} cy={19.8} r={2.1} fill={INK} />
      <rect x={15.2} y={20.4} width={1.6} height={4} rx={0.8} fill={INK} />
      {gloss('M9.2 17.2v5', 1.5)}
    </>
  ),
  calendar: (k) => (
    <>
      {shape(k, 'paper', 'M8.5 7h15A3.5 3.5 0 0 1 27 10.5v13a3.5 3.5 0 0 1-3.5 3.5h-15A3.5 3.5 0 0 1 5 23.5v-13A3.5 3.5 0 0 1 8.5 7z')}
      <path d="M5.8 10.5A2.8 2.8 0 0 1 8.6 7.8h14.8a2.8 2.8 0 0 1 2.8 2.7V13H5.8z" fill={k.f('red')} />
      <path d="M11 4.5v5M21 4.5v5" stroke={TONES.slate[2]} strokeWidth={2.4} strokeLinecap="round" />
      <circle cx={11} cy={17.6} r={1.4} fill="#9aa6c8" />
      <circle cx={16} cy={17.6} r={1.4} fill="#9aa6c8" />
      <circle cx={21} cy={17.6} r={1.4} fill="#9aa6c8" />
      <circle cx={11} cy={22.2} r={1.4} fill="#9aa6c8" />
      <circle cx={16} cy={22.2} r={1.4} fill="#9aa6c8" />
      <circle cx={21} cy={22.2} r={2.1} fill="#ff3b5c" />
    </>
  ),
  gift: (k) => (
    <>
      <path d="M16 10.4c-1.6-3.6-6.8-5.2-7.4-2.2-.5 2.8 4.6 2.2 7.4 2.2zM16 10.4c1.6-3.6 6.8-5.2 7.4-2.2.5 2.8-4.6 2.2-7.4 2.2z" fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.2} strokeLinejoin="round" />
      {shape(k, 'pink', 'M6.5 15h19v10.5a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2z')}
      {shape(k, 'pink', 'M5.5 10.4h21a1.6 1.6 0 0 1 1.6 1.6v2.6a1.6 1.6 0 0 1-1.6 1.6h-21a1.6 1.6 0 0 1-1.6-1.6V12a1.6 1.6 0 0 1 1.6-1.6z')}
      <rect x={14} y={10.4} width={4} height={17.1} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={0.9} />
      {gloss('M8.4 18v5.4', 1.5)}
    </>
  ),
  target: (k) => (
    <>
      <circle cx={15} cy={17} r={11.6} fill={k.f('red')} stroke={k.e('red')} strokeWidth={1.5} />
      <circle cx={15} cy={17} r={7.8} fill="#fff" />
      <circle cx={15} cy={17} r={4.4} fill={k.f('red')} />
      <circle cx={15} cy={17} r={1.4} fill="#fff" />
      <path d="M15 17 26.6 5.4" stroke={INK} strokeWidth={1.8} strokeLinecap="round" />
      <path d="M24.4 3.6l2.4 1.8 1.8 2.4-2.6.4-2-2z" fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={0.9} strokeLinejoin="round" />
    </>
  ),
  bell: (k) => (
    <>
      {shape(k, 'gold', 'M16 4.5a1.8 1.8 0 0 1 1.8 1.8v.7c3.8.9 6 4.1 6 8.1v4.4l2.4 3.3H5.8l2.4-3.3V15c0-4 2.2-7.2 6-8.1v-.7A1.8 1.8 0 0 1 16 4.5z')}
      {shape(k, 'amber', 'M12.8 24.6a3.2 3.2 0 0 0 6.4 0z', 1.2)}
      {gloss('M11 17.6v-2.8a5 5 0 0 1 2.6-4.4', 1.6)}
    </>
  ),
  heart: (k) => (
    <>
      {shape(k, 'red', 'M16 27.5S4 20.4 4 12.2A6.2 6.2 0 0 1 16 9.6a6.2 6.2 0 0 1 12 2.6c0 8.2-12 15.3-12 15.3z')}
      {gloss('M7.6 11.6a3 3 0 0 1 2.6-2.2', 2)}
    </>
  ),
  eye: (k) => (
    <>
      {shape(k, 'paper', 'M2.5 16S7.5 7.5 16 7.5 29.5 16 29.5 16 24.5 24.5 16 24.5 2.5 16 2.5 16z')}
      <circle cx={16} cy={16} r={5.6} fill={k.f('sky')} stroke={k.e('sky')} strokeWidth={1.2} />
      <circle cx={16} cy={16} r={2.5} fill={INK} />
      <circle cx={17.7} cy={14.3} r={1.2} fill="#fff" />
    </>
  ),
  timer: (k) => (
    <>
      {shape(k, 'silver', 'M14 2.6h4a1 1 0 0 1 1 1v2h-6v-2a1 1 0 0 1 1-1z', 1.1)}
      <circle cx={16} cy={18} r={10.6} fill={k.f('paper')} stroke={k.e('silver')} strokeWidth={1.8} />
      <path d="M16 18v-6.4M16 18l4 2.4" stroke="#ff3b5c" strokeWidth={2} strokeLinecap="round" />
      <circle cx={16} cy={18} r={1.5} fill={INK} />
      {gloss('M9 15.4a7.6 7.6 0 0 1 3.6-4.6', 1.4, 0.9)}
    </>
  ),
  hourglass: (k) => (
    <>
      <path d="M9 6.5h14c0 5-7 6.5-7 9.5s7 4.5 7 9.5H9c0-5 7-6.5 7-9.5S9 11.5 9 6.5z" fill="#d9ecff" fillOpacity={0.35} stroke={TONES.sky[2]} strokeWidth={1.2} />
      <path d="M11.6 9h8.8c-.8 2.4-4.4 3.6-4.4 5.4 0-1.8-3.6-3-4.4-5.4z" fill={k.f('gold')} />
      <path d="M10.4 25.2c.8-3 5.6-4 5.6-6 0 2 4.8 3 5.6 6z" fill={k.f('gold')} />
      {shape(k, 'amber', 'M8.5 3.5h15a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3zM8.5 25.5h15a1.5 1.5 0 0 1 0 3h-15a1.5 1.5 0 0 1 0-3z', 1.2)}
    </>
  ),
  shield: (k) => (
    <>
      {shape(k, 'blue', 'M16 3.5 26 7.5v7.4c0 6.6-4.2 10.8-10 13.6-5.8-2.8-10-7-10-13.6V7.5z')}
      <path d="M16 7.4 22.6 10v5c0 4.4-2.6 7.4-6.6 9.4-4-2-6.6-5-6.6-9.4v-5z" fill="none" stroke="#fff" strokeWidth={1.4} opacity={0.45} />
      {gloss('M9.4 10.2v4', 1.6)}
    </>
  ),
  magnet: (k) => (
    <>
      {shape(k, 'red', 'M5.5 5h6.5v10a4 4 0 0 0 8 0V5h6.5v10a10.5 10.5 0 0 1-21 0z')}
      {shape(k, 'silver', 'M5.5 5H12v4.6H5.5zM20 5h6.5v4.6H20z', 1.2)}
      {gloss('M8 13v2.6a7 7 0 0 0 1.6 4.4', 1.5)}
    </>
  ),
  ghost: (k) => (
    <>
      {shape(k, 'ghost', 'M16 3.5c6 0 10 4.4 10 10.2V27l-3.3-2.4L19.4 27 16 24.6 12.6 27l-3.3-2.4L6 27V13.7C6 7.9 10 3.5 16 3.5z')}
      <ellipse cx={12.6} cy={13.6} rx={1.7} ry={2.3} fill={INK} />
      <ellipse cx={19.4} cy={13.6} rx={1.7} ry={2.3} fill={INK} />
      <ellipse cx={16} cy={19} rx={1.6} ry={1.9} fill={INK} opacity={0.8} />
    </>
  ),
  snowflake: () => (
    <g strokeLinecap="round">
      <path d="M16 3.5v25M5.2 9.8l21.6 12.4M5.2 22.2l21.6-12.4M12.8 4.6 16 7.8l3.2-3.2M12.8 27.4 16 24.2l3.2 3.2" stroke={TONES.cyan[2]} strokeWidth={4.4} fill="none" strokeLinejoin="round" />
      <path d="M16 3.5v25M5.2 9.8l21.6 12.4M5.2 22.2l21.6-12.4M12.8 4.6 16 7.8l3.2-3.2M12.8 27.4 16 24.2l3.2 3.2" stroke="#c4f8ff" strokeWidth={2.2} fill="none" strokeLinejoin="round" />
    </g>
  ),
  skull: (k) => (
    <>
      {shape(k, 'paper', 'M16 3.5c6.6 0 11 4.4 11 10.4 0 3.6-1.8 6-4 7.4V26a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2v-4.7c-2.2-1.4-4-3.8-4-7.4C5 7.9 9.4 3.5 16 3.5z')}
      <circle cx={11.6} cy={14.4} r={3.1} fill={INK} />
      <circle cx={20.4} cy={14.4} r={3.1} fill={INK} />
      <path d="M16 18.4l-1.5 2.8h3z" fill={INK} />
      <path d="M13.4 24v4M16 24v4M18.6 24v4" stroke={TONES.paper[2]} strokeWidth={1.1} />
    </>
  ),
  star: (k) => (
    <>
      {shape(k, 'gold', 'M16 3.5l3.7 7.6 8.3 1.2-6 5.8 1.4 8.3L16 22.5l-7.4 3.9 1.4-8.3-6-5.8 8.3-1.2z')}
      {gloss('M12.6 13.2l1.8-3.6', 1.6)}
    </>
  ),
  sparkle: (k) => (
    <>
      {shape(k, 'gold', 'M13.5 3c1 6 3 8 9 9-6 1-8 3-9 9-1-6-3-8-9-9 6-1 8-3 9-9z')}
      {shape(k, 'gold', 'M24.5 17.5c.5 2.8 1.4 3.7 4 4.2-2.6.5-3.5 1.4-4 4.2-.5-2.8-1.4-3.7-4-4.2 2.6-.5 3.5-1.4 4-4.2z', 1)}
    </>
  ),
  chat: (k) => (
    <>
      {shape(k, 'sky', 'M6.5 5.5h19A3.5 3.5 0 0 1 29 9v10a3.5 3.5 0 0 1-3.5 3.5H14l-6 5v-5H6.5A3.5 3.5 0 0 1 3 19V9a3.5 3.5 0 0 1 3.5-3.5z')}
      <circle cx={10.5} cy={14} r={1.8} fill={TONES.sky[2]} />
      <circle cx={16} cy={14} r={1.8} fill={TONES.sky[2]} />
      <circle cx={21.5} cy={14} r={1.8} fill={TONES.sky[2]} />
    </>
  ),
  ticket: (k) => (
    <>
      {shape(k, 'purple', 'M4 9.5A2.5 2.5 0 0 1 6.5 7h19A2.5 2.5 0 0 1 28 9.5v3a3.5 3.5 0 0 0 0 7v3a2.5 2.5 0 0 1-2.5 2.5h-19A2.5 2.5 0 0 1 4 22.5v-3a3.5 3.5 0 0 0 0-7z')}
      <path d="M20.5 9v14" stroke="#fff" strokeWidth={1.4} strokeDasharray="1.6 2" opacity={0.6} />
      <path d="M12.5 11.4l1.4 2.9 3.2.5-2.3 2.2.5 3.1-2.8-1.5-2.8 1.5.5-3.1-2.3-2.2 3.2-.5z" fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={0.8} strokeLinejoin="round" />
    </>
  ),
  wave: (k) => (
    <>
      {shape(k, 'blue', 'M3 26c1.4-9 7.4-16 15-16 4.6 0 7.8 3 7.8 6.6 0 2.8-2.2 4.6-4.6 4.6-2.2 0-3.6-1.6-3.4-3.4 2 .6 3.8-.4 3.6-2.2-.2-2.2-3-3-5.2-2.4C11.4 14.6 9 20 8.6 26z')}
      {shape(k, 'cyan', 'M3 26c2.4-1.8 4.8-1.8 7.2 0s4.8 1.8 7.2 0 4.8-1.8 7.2 0 3.6 1.4 4.4.6V29H3z', 1.2)}
      {gloss('M9.6 18.6a10 10 0 0 1 4.6-5', 1.6)}
    </>
  ),
  sliders: (k) => (
    <>
      <path d="M9 5v22M16 5v22M23 5v22" stroke="#3a4570" strokeWidth={2.6} strokeLinecap="round" />
      <circle cx={9} cy={20} r={3.6} fill={k.f('cyan')} stroke={k.e('cyan')} strokeWidth={1.3} />
      <circle cx={16} cy={11} r={3.6} fill={k.f('gold')} stroke={k.e('gold')} strokeWidth={1.3} />
      <circle cx={23} cy={17} r={3.6} fill={k.f('pink')} stroke={k.e('pink')} strokeWidth={1.3} />
    </>
  ),
  film: (k) => (
    <>
      {shape(k, 'slate', 'M6.5 13h19a2.5 2.5 0 0 1 2.5 2.5v9a2.5 2.5 0 0 1-2.5 2.5h-19A2.5 2.5 0 0 1 4 24.5v-9A2.5 2.5 0 0 1 6.5 13z')}
      {shape(k, 'paper', 'M4.4 12.2 26 6.6l1 3.8L5.4 16z', 1.2)}
      <path d="M9 10.9l2.4 3.4M14.6 9.5 17 12.9M20.2 8l2.4 3.4" stroke={INK} strokeWidth={1.6} />
      <path d="M14 17.6v6l5-3z" fill="#fff" />
    </>
  ),
  phone: (k) => (
    <>
      {shape(k, 'slate', 'M12.5 3h7A3.5 3.5 0 0 1 23 6.5v19a3.5 3.5 0 0 1-3.5 3.5h-7A3.5 3.5 0 0 1 9 25.5v-19A3.5 3.5 0 0 1 12.5 3z')}
      <rect x={11} y={6.4} width={10} height={16.6} rx={1.6} fill={k.f('sky')} />
      <circle cx={16} cy={26} r={1.1} fill={INK} />
    </>
  ),
  bug: (k) => (
    <>
      <path d="M8 13.5 4.5 11.5M24 13.5l3.5-2M7.5 19H3.5M24.5 19h4M8 24l-3.5 2.5M24 24l3.5 2.5" stroke={INK} strokeWidth={1.8} strokeLinecap="round" />
      <circle cx={16} cy={9} r={4} fill={INK} />
      {shape(k, 'green', 'M16 10.5c4.6 0 8 3.8 8 9s-3.4 8.5-8 8.5-8-3.3-8-8.5 3.4-9 8-9z')}
      <path d="M16 11v16.6" stroke={TONES.green[2]} strokeWidth={1.2} />
      <circle cx={12.4} cy={18} r={1.5} fill={INK} opacity={0.75} />
      <circle cx={19.6} cy={21.6} r={1.5} fill={INK} opacity={0.75} />
    </>
  ),
  bulb: (k) => (
    <>
      {shape(k, 'yellow', 'M16 3.5a8.5 8.5 0 0 0-5 15.4c.9.7 1.5 1.8 1.5 3v.6h7v-.6c0-1.2.6-2.3 1.5-3A8.5 8.5 0 0 0 16 3.5z')}
      {shape(k, 'silver', 'M12.5 22.5h7v3.2a1.8 1.8 0 0 1-1.8 1.8h-3.4a1.8 1.8 0 0 1-1.8-1.8z', 1.2)}
      {gloss('M11.2 12a5 5 0 0 1 3-4', 1.8)}
    </>
  ),
  mail: (k) => (
    <>
      {shape(k, 'paper', 'M6.5 7.5h19a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3h-19a3 3 0 0 1-3-3v-11a3 3 0 0 1 3-3z')}
      <path d="M4.6 9.2 16 18l11.4-8.8" fill="none" stroke={TONES.paper[2]} strokeWidth={1.6} strokeLinejoin="round" />
      <circle cx={24.5} cy={8.5} r={3.8} fill={k.f('red')} stroke={k.e('red')} strokeWidth={1.1} />
    </>
  ),
  party: (k) => (
    <>
      {shape(k, 'gold', 'M4.5 27.5 10 11.5l10.5 10.5z')}
      <path d="M7.2 19.6l5.2 5.2M8.8 15l8.2 8.2" stroke={k.e('gold')} strokeWidth={1.2} opacity={0.6} />
      <path d="M15 9c1.4-2.6 4-3 4.6-5.6M20.5 13c2.6-1.4 5.2-.2 7.5-2.2" fill="none" stroke="#ff4f9a" strokeWidth={1.8} strokeLinecap="round" />
      <circle cx={23.5} cy={5.5} r={1.5} fill="#34f5a0" />
      <circle cx={26.5} cy={16.5} r={1.4} fill="#2f7dff" />
      <rect x={18.6} y={7.2} width={2.6} height={2.6} rx={0.5} fill="#ffd166" transform="rotate(20 19.9 8.5)" />
      <circle cx={13} cy={5} r={1.2} fill="#8b4dff" />
    </>
  ),
  warning: (k) => (
    <>
      {shape(k, 'yellow', 'M14.3 4.9a2 2 0 0 1 3.4 0l11 19.2a2 2 0 0 1-1.7 3H5a2 2 0 0 1-1.7-3z')}
      <path d="M16 11.5v7" stroke={INK} strokeWidth={2.8} strokeLinecap="round" />
      <circle cx={16} cy={22.6} r={1.6} fill={INK} />
    </>
  ),
  ruler: (k) => (
    <>
      {shape(k, 'yellow', 'M3.6 21.4 21.4 3.6a1.4 1.4 0 0 1 2 0l5 5a1.4 1.4 0 0 1 0 2L10.6 28.4a1.4 1.4 0 0 1-2 0l-5-5a1.4 1.4 0 0 1 0-2z')}
      <path d="M8.4 16.6l2.4 2.4M11.6 13.4l3.6 3.6M14.8 10.2l2.4 2.4M18 7l3.6 3.6" stroke={INK} strokeWidth={1.4} strokeLinecap="round" opacity={0.7} />
    </>
  ),
  apple: (k) => (
    <>
      <path d="M16 9.4c.2-2.4 1.2-4.4 3.2-5.6" fill="none" stroke="#6b3d1f" strokeWidth={1.8} strokeLinecap="round" />
      <path d="M17.6 7.2c1.6-2.6 4.8-3 6.6-2-1.2 2.6-4 3.4-6.6 2z" fill={k.f('fLime')} stroke={k.e('fLime')} strokeWidth={1} strokeLinejoin="round" />
      {shape(k, 'fRed', 'M16 10c2.6-1.8 7.6-1.8 9.4 2.6 1.8 4.4-.6 11-3.4 13.4-1.8 1.6-3.6.8-6 .8s-4.2.8-6-.8c-2.8-2.4-5.2-9-3.4-13.4C8.4 8.2 13.4 8.2 16 10z')}
      {gloss('M9.8 14.6c.2-1.6 1-2.6 2.2-3.2', 2, 0.85)}
    </>
  ),
  check: (k) => (
    <>
      <circle cx={16} cy={16} r={12.4} fill={k.f('green')} stroke={k.e('green')} strokeWidth={1.5} />
      <path d="M10.2 16.4l4 4 7.6-8" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  bars: (k) => (
    <>
      {shape(k, 'bronze', 'M4 18h7v10H4z', 1.3)}
      {shape(k, 'gold', 'M12.5 8h7v20h-7z', 1.3)}
      {shape(k, 'silver', 'M21 13h7v15h-7z', 1.3)}
    </>
  ),
  noads: (k) => (
    <>
      <circle cx={16} cy={16} r={12.4} fill={k.f('red')} stroke={k.e('red')} strokeWidth={1.5} />
      <circle cx={16} cy={16} r={8.2} fill="#fff" />
      <path d="M10.2 21.8 21.8 10.2" stroke={TONES.red[1]} strokeWidth={3} />
    </>
  ),
  // ----- Food: the arena's fruit and treats (also drawn on the game canvas, see foodArt) -----
  cherries: (k) => (
    <>
      <path d="M10.6 17.6c1.2-6 4.4-10 9.6-13M21.4 18.4c.2-5-.2-9-1.2-13.8" fill="none" stroke="#6b3d1f" strokeWidth={1.7} strokeLinecap="round" />
      <path d="M20.2 4.8c2.6-1.8 6.2-1.4 7.6.4-2.2 2-5.4 2-7.6-.4z" fill={k.f('fLime')} stroke={k.e('fLime')} strokeWidth={1} strokeLinejoin="round" />
      <circle cx={10.4} cy={22} r={6.2} fill={k.f('fCherry')} stroke={k.e('fCherry')} strokeWidth={1.5} />
      <circle cx={21.6} cy={22.6} r={6.2} fill={k.f('fCherry')} stroke={k.e('fCherry')} strokeWidth={1.5} />
      {gloss('M6.8 20.6a3.6 3.6 0 0 1 2.4-2.4', 1.6, 0.8)}
      {gloss('M18 21.2a3.6 3.6 0 0 1 2.4-2.4', 1.6, 0.8)}
    </>
  ),
  strawberry: (k) => (
    <>
      {shape(k, 'fRed', 'M16 29c-5.6-2-10-7.6-10-13.2C6 11.4 9.6 9 16 9s10 2.4 10 6.8C26 21.4 21.6 27 16 29z')}
      <g fill="#fff3b8">
        <ellipse cx={11} cy={15} rx={0.8} ry={1.1} />
        <ellipse cx={16} cy={14.2} rx={0.8} ry={1.1} />
        <ellipse cx={21} cy={15} rx={0.8} ry={1.1} />
        <ellipse cx={13.4} cy={19.4} rx={0.8} ry={1.1} />
        <ellipse cx={18.6} cy={19.4} rx={0.8} ry={1.1} />
        <ellipse cx={16} cy={24} rx={0.8} ry={1.1} />
      </g>
      {shape(k, 'fLime', 'M9.4 10.6 11.8 6.4l2.4 2.8L16 4.6l1.8 4.6 2.4-2.8 2.4 4.2c-2 1.6-4 2.2-6.6 2.2s-4.6-.6-6.6-2.2z', 1.2)}
      {gloss('M8.6 16.4c.2-1.6.8-2.6 1.8-3.4', 1.8, 0.8)}
    </>
  ),
  grapes: (k) => (
    <>
      <path d="M16 8.4V4" stroke="#6b3d1f" strokeWidth={1.8} strokeLinecap="round" />
      <path d="M16 6.2c2-2.6 5.6-3.2 7.8-1.8-1.6 2.6-5.2 3.4-7.8 1.8z" fill={k.f('fLime')} stroke={k.e('fLime')} strokeWidth={1} strokeLinejoin="round" />
      {(
        [
          [10, 11.8],
          [16, 11.2],
          [22, 11.8],
          [13, 17.2],
          [19, 17.2],
          [16, 22.8],
        ] as const
      ).map(([x, y]) => (
        <g key={`${x},${y}`}>
          <circle cx={x} cy={y} r={4} fill={k.f('fGrape')} stroke={k.e('fGrape')} strokeWidth={1.2} />
          <circle cx={x - 1.3} cy={y - 1.4} r={1} fill="#fff" opacity={0.7} />
        </g>
      ))}
    </>
  ),
  orange: (k) => (
    <>
      <circle cx={16} cy={17.6} r={11} fill={k.f('fOrange')} stroke={k.e('fOrange')} strokeWidth={1.5} />
      <g fill={TONES.fOrange[2]} opacity={0.22}>
        <circle cx={12} cy={15} r={0.7} />
        <circle cx={19} cy={13.6} r={0.7} />
        <circle cx={21.4} cy={20} r={0.7} />
        <circle cx={14.6} cy={22.6} r={0.7} />
        <circle cx={10} cy={20.4} r={0.7} />
      </g>
      <path d="M16 6.8c1.6-2.8 5-3.6 7.4-2.4-1.4 2.8-4.8 3.6-7.4 2.4z" fill={k.f('fLime')} stroke={k.e('fLime')} strokeWidth={1} strokeLinejoin="round" />
      <circle cx={16} cy={7} r={1.1} fill="#6b3d1f" />
      {gloss('M8.6 15a7.6 7.6 0 0 1 3.4-4.4', 2, 0.8)}
    </>
  ),
  banana: (k) => (
    <>
      {shape(k, 'fYellow', 'M5.2 9.4c1.6 9.6 8.6 16.2 19.2 15.6 2.4-.1 3.6-1.4 3-2.4-.5-.8-1.8-.7-3.2-.7-7.8 0-13.4-5.2-15.4-12.6z')}
      <path d="M3.8 8.2l1.8-.8 1.4 2.4-2 .8z" fill="#6b3d1f" />
      <path d="M9.4 11.6c2.6 6.6 7.4 10.4 14 10.8" fill="none" stroke={TONES.fYellow[2]} strokeWidth={1} opacity={0.45} />
      <path d="M26.6 22.4l1.6.6" stroke="#6b3d1f" strokeWidth={1.6} strokeLinecap="round" />
      {gloss('M8.2 12.4c1.4 3.2 3.4 5.6 5.8 7.2', 1.6, 0.8)}
    </>
  ),
  watermelon: (k) => (
    <g transform="translate(0 2)">
      {shape(k, 'fGreen', 'M3.4 10.6h25.2a12.6 12.6 0 0 1-25.2 0z')}
      <path d="M5.6 10.6h20.8a10.4 10.4 0 0 1-20.8 0z" fill="#f1ffe6" />
      <path d="M6.8 10.6h18.4a9.2 9.2 0 0 1-18.4 0z" fill={k.f('fRed')} />
      <g fill={INK}>
        <ellipse cx={11.6} cy={13.6} rx={0.8} ry={1.3} transform="rotate(-25 11.6 13.6)" />
        <ellipse cx={16} cy={16.4} rx={0.8} ry={1.3} />
        <ellipse cx={20.4} cy={13.6} rx={0.8} ry={1.3} transform="rotate(25 20.4 13.6)" />
        <ellipse cx={16} cy={12.4} rx={0.8} ry={1.3} />
      </g>
      {gloss('M8.6 12.2h14.8', 1.2, 0.55)}
    </g>
  ),
  pear: (k) => (
    <>
      <path d="M16 7.4c.2-2 .8-3.4 2-4.4" fill="none" stroke="#6b3d1f" strokeWidth={1.7} strokeLinecap="round" />
      {shape(k, 'fLime', 'M16 7c2.6 0 4 2.2 4.4 5 .4 2.4 1.4 3.4 3.4 5.4 2.4 2.4 3 5.4 1.6 8-1.6 3-5 4-9.4 4s-7.8-1-9.4-4c-1.4-2.6-.8-5.6 1.6-8 2-2 3-3 3.4-5.4C12 9.2 13.4 7 16 7z')}
      <path d="M17.6 6c1.4-2 4.2-2.6 6-1.6-1.2 2-4 2.6-6 1.6z" fill={k.f('fGreen')} stroke={k.e('fGreen')} strokeWidth={0.9} strokeLinejoin="round" />
      {gloss('M9.6 20.6a6 6 0 0 1 2.4-3.6', 1.8, 0.8)}
    </>
  ),
  peach: (k) => (
    <>
      {shape(k, 'fPeach', 'M16 9c6.4-2.4 11.6 1.8 11.6 8.6 0 6.4-5 11-11.6 11S4.4 24 4.4 17.6C4.4 10.8 9.6 6.6 16 9z')}
      <path d="M16 9.6c-2.2 4.4-2.2 11 0 18.4" fill="none" stroke={TONES.fPeach[2]} strokeWidth={1.2} opacity={0.4} />
      <path d="M16.4 8.8c1.2-3 4.4-4.4 7.2-3.6-1 3-4.2 4.4-7.2 3.6z" fill={k.f('fGreen')} stroke={k.e('fGreen')} strokeWidth={1} strokeLinejoin="round" />
      {gloss('M7.8 15.4a5.6 5.6 0 0 1 3.4-3.6', 2, 0.8)}
    </>
  ),
  carrot: (k) => (
    <>
      <path d="M23 9.4c.4-3 2.6-5.6 5.4-6-.2 2.8-2.4 5.2-5.4 6zM24.2 10.6c2.2-1.4 5.2-1.4 6.6.4-2 1.4-4.8 1.4-6.6-.4zM21.8 9c-1-2.6-.2-5.6 1.8-7 1 2.4.4 5.4-1.8 7z" fill={k.f('fLime')} stroke={k.e('fLime')} strokeWidth={1} strokeLinejoin="round" />
      {shape(k, 'fOrange', 'M24.4 9.2c2 2 1.6 5-.6 7.2L9.6 28.6c-.8.7-2 .2-1.8-.9l2.8-14.3c.6-2.4 2-3.8 4.4-4.6 3.4-1.2 7.4-1.6 9.4.4z')}
      <path d="M14.4 14.4l3 1.2M12.8 19.4l3 1M11.4 23.8l2.4.8" stroke={TONES.fOrange[2]} strokeWidth={1.2} strokeLinecap="round" opacity={0.5} />
      {gloss('M13.2 12.4c1.4-1 3-1.6 4.6-1.8', 1.6, 0.75)}
    </>
  ),
  donut: (k) => (
    <>
      <path d="M16 3.8a12.2 12.2 0 1 1 0 24.4 12.2 12.2 0 1 1 0-24.4zM16 12.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 1 0 0-6.8z" fillRule="evenodd" fill={k.f('fDough')} stroke={k.e('fDough')} strokeWidth={1.5} />
      <path
        d="M16 6.2c3 0 4.4 1.4 6.6 1.8 2.4.6 3.4 2.8 3.4 5.2 0 1.6 1.2 2.6 1 4.4-.4 2.6-2.4 3.4-3.2 5.4-.8 2.2-3 3.4-5.6 3.4-1.4 0-2.2.8-3.8.6-2.8-.4-3.8-2.6-5.8-3.6-2.2-1-3-3.2-3-5.6 0-1.6-.8-2.4-.4-4.2.6-2.4 2.6-3 3.6-5 1.2-2.2 4-2.4 7.2-2.4zM16 11.6a4.4 4.4 0 1 0 0 8.8 4.4 4.4 0 1 0 0-8.8z"
        fillRule="evenodd"
        fill={k.f('fPink')}
        stroke={k.e('fPink')}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <g strokeWidth={1.5} strokeLinecap="round">
        <path d="M10.4 10.4l1.6.9" stroke="#fff" />
        <path d="M20.6 9l.4 1.6" stroke="#34f5a0" />
        <path d="M23.4 15.6l-1 1.4" stroke="#ffd166" />
        <path d="M9 17.4l1.6-.4" stroke="#2f7dff" />
        <path d="M14 23.4l1.6.4" stroke="#fff" />
        <path d="M20.4 22l1-1.2" stroke="#34f5a0" />
      </g>
    </>
  ),
  cookie: (k) => (
    <>
      <circle cx={16} cy={16} r={12.2} fill={k.f('fDough')} stroke={k.e('fDough')} strokeWidth={1.5} />
      <g fill="#5a3215">
        <circle cx={11} cy={11} r={2} />
        <circle cx={19.6} cy={10} r={1.7} />
        <circle cx={21.4} cy={18.6} r={2.2} />
        <circle cx={13} cy={20.6} r={1.9} />
        <circle cx={16.6} cy={15.4} r={1.4} />
        <circle cx={9.4} cy={16.6} r={1.2} />
        <circle cx={18} cy={24} r={1.2} />
      </g>
      {gloss('M7.4 12.4a9 9 0 0 1 3-4', 1.8, 0.6)}
    </>
  ),
  cupcake: (k) => (
    <>
      {shape(k, 'fSky', 'M7.6 17.2h16.8l-2.2 10.9a1.4 1.4 0 0 1-1.4 1.1h-9.6a1.4 1.4 0 0 1-1.4-1.1z')}
      <path d="M12 18l1 10.6M16 18v10.6M20 18l-1 10.6" stroke={TONES.fSky[2]} strokeWidth={1} opacity={0.45} />
      {shape(k, 'fPink', 'M7 17.6c-1.8-.4-2-3.4 0-4.4.4-3.4 3.4-5.4 6.4-4.8C14.6 5.8 17.6 5 20 6.6c2.6-.2 4.8 2 4.6 4.6 2.2 1 2.6 3.4 1.6 5.2-.4.8-1.2 1.2-2 1.2z')}
      <circle cx={17.4} cy={5.2} r={2.5} fill={k.f('fCherry')} stroke={k.e('fCherry')} strokeWidth={1} />
      <g strokeWidth={1.4} strokeLinecap="round">
        <path d="M10.6 12.6l1.2.8" stroke="#fff" />
        <path d="M15.4 11l.2 1.4" stroke="#ffd166" />
        <path d="M20.8 12.8l-1 .9" stroke="#34f5a0" />
        <path d="M12.4 15.6h1.4" stroke="#2f7dff" />
      </g>
      {gloss('M8.4 14.6c.4-1.8 1.4-3 3-3.6', 1.6, 0.7)}
    </>
  ),
  candy: (k) => (
    <>
      {shape(k, 'fCyan', 'M9.4 16 3.2 10.4c-1 2.4-1 8.8 0 11.2zM22.6 16l6.2-5.6c1 2.4 1 8.8 0 11.2z', 1.3)}
      <ellipse cx={16} cy={16} rx={8} ry={6.6} fill={k.f('fCyan')} stroke={k.e('fCyan')} strokeWidth={1.5} />
      <path d="M12.4 10.4c-1.4 3.4-1 7.4 1 11M17.6 9.6c-1.4 3.6-1 8.2 1 12.2" fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" opacity={0.75} />
    </>
  ),
  lollipop: (k) => (
    <>
      <path d="M16 19v10.4" stroke="#7f8bab" strokeWidth={3.4} strokeLinecap="round" />
      <path d="M16 19v10.4" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
      <circle cx={16} cy={12} r={9.8} fill={k.f('fPink')} stroke={k.e('fPink')} strokeWidth={1.5} />
      <path d="M16 12.4c0-1 1.5-1.4 2.2-.6 1.2 1.2.2 3.4-1.7 3.6-2.9.4-4.6-2.7-3.4-5.3 1.5-3.4 6.3-4 8.4-1.1 2.1 2.8.9 7.2-2.6 8.4" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" opacity={0.9} />
    </>
  ),
  pizza: (k) => (
    <>
      {shape(k, 'fCheese', 'M5.4 11.4c6.8-3.6 14.4-3.6 21.2 0L16.9 29a1 1 0 0 1-1.8 0z')}
      <circle cx={12.2} cy={14.4} r={2.3} fill={k.f('fRed')} stroke={k.e('fRed')} strokeWidth={0.8} />
      <circle cx={19.8} cy={14.8} r={2.3} fill={k.f('fRed')} stroke={k.e('fRed')} strokeWidth={0.8} />
      <circle cx={16} cy={21} r={2} fill={k.f('fRed')} stroke={k.e('fRed')} strokeWidth={0.8} />
      {shape(k, 'fDough', 'M3.8 8.2c7.6-4.4 16.8-4.4 24.4 0l-1.6 3.4c-6.8-3.8-14.4-3.8-21.2 0z')}
    </>
  ),
  speed: (k) => (
    <>
      <path d="M7 8l8 8-8 8M16.4 8l8 8-8 8" fill="none" stroke={k.e('lime')} strokeWidth={5.4} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 8l8 8-8 8M16.4 8l8 8-8 8" fill="none" stroke={k.f('lime')} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
};

export type GameIconName = keyof typeof ICONS;

export function hasGameIcon(name: string): boolean {
  return name in ICONS;
}

/** `size`: pixels, or a CSS length such as '1.2em' to sit in a line of text at the text's size. */
export function GameIcon({ name, size = 24, className, title }: { name: GameIconName | string; size?: number | string; className?: string; title?: string }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const draw = ICONS[name];
  if (!draw) return null;
  const used = new Set<Tone>();
  const kit: Kit = {
    f: (t) => {
      used.add(t);
      return `url(#${id}${t})`;
    },
    e: (t) => TONES[t][2],
    uid: (n) => `${id}-${n}`,
  };
  const body = draw(kit);
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" className={`gi${className ? ` ${className}` : ''}`} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      <defs>
        {[...used].map((t) => (
          <linearGradient key={t} id={`${id}${t}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={TONES[t][0]} />
            <stop offset="1" stopColor={TONES[t][1]} />
          </linearGradient>
        ))}
      </defs>
      {body}
    </svg>
  );
}

/** Emoji the game used as icons, and the drawn icon that replaces each. Anything else stays as text. */
const EMOJI_ICON: Record<string, GameIconName> = {
  '🪙': 'coin', '💎': 'gem', '🏆': 'trophy', '👑': 'crown', '🔱': 'crown', '🥇': 'medal1', '🥈': 'medal2', '🥉': 'medal3',
  '🏅': 'medalStar', '🎖️': 'medalStar', '🌍': 'globe', '🐍': 'snake', '🐉': 'snake', '⚡': 'bolt', '🔥': 'flame', '💥': 'burst',
  '⚔️': 'swords', '👥': 'users', '🤝': 'users', '🌀': 'spiral', '🎮': 'gamepad', '🔑': 'key', '🤖': 'robot', '🎨': 'palette',
  '🛒': 'bag', '🏰': 'castle', '📺': 'tv', '💾': 'cloud', '🔒': 'lock', '🔓': 'unlock', '📅': 'calendar', '🎁': 'gift',
  '🎯': 'target', '🔔': 'bell', '❤️': 'heart', '👁': 'eye', '👁️': 'eye', '⏱': 'timer', '⏱️': 'timer', '⏳': 'hourglass',
  '🛡️': 'shield', '🧲': 'magnet', '👻': 'ghost', '❄️': 'snowflake', '💀': 'skull', '🌟': 'star', '✨': 'sparkle', '🎆': 'sparkle',
  '💬': 'chat', '🎟️': 'ticket', '🌊': 'wave', '🎛️': 'sliders', '🎬': 'film', '📲': 'phone', '📱': 'phone', '🐞': 'bug',
  '💡': 'bulb', '✉️': 'mail', '🎉': 'party', '⚠️': 'warning', '📏': 'ruler', '🍎': 'apple', '✅': 'check', '🚫': 'noads',
};

/** Normalizes the variation selector, so '⚔' and '⚔️' find the same icon. */
export function iconForEmoji(emoji: string): GameIconName | null {
  return EMOJI_ICON[emoji] ?? EMOJI_ICON[`${emoji}️`] ?? EMOJI_ICON[emoji.replace(/️/g, '')] ?? null;
}

/** An icon given as an emoji (game data stores emoji): the drawn icon when there is one, else the emoji. */
export function Glyph({ e, size = '1.2em', className }: { e: string; size?: number | string; className?: string }) {
  const name = iconForEmoji(e);
  return name ? <GameIcon name={name} size={size} className={className} /> : <span className={className} aria-hidden="true">{e}</span>;
}

/** JSX prop names whose SVG attribute isn't simply the kebab-case of the name. */
const SVG_ATTR: Record<string, string> = { className: 'class' };
const escapeXml = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/** The drawn JSX as SVG markup (only plain SVG elements and fragments occur in the icons). */
function toSvg(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return escapeXml(String(node));
  if (Array.isArray(node)) return node.map(toSvg).join('');
  if (typeof node !== 'object' || !('type' in node)) return '';
  const { type, props } = node as { type: unknown; props: Record<string, unknown> & { children?: ReactNode } };
  if (typeof type !== 'string') return toSvg(props.children);
  let attrs = '';
  for (const [k, v] of Object.entries(props)) {
    if (k === 'children' || v === undefined || v === null || v === false) continue;
    attrs += ` ${SVG_ATTR[k] ?? k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeXml(String(v))}"`;
  }
  return `<${type}${attrs}>${toSvg(props.children)}</${type}>`;
}

/**
 * A standalone SVG document of an icon, `px` square: the game canvas draws the same art as the
 * menus by loading this as an image (see game/foodArt). Null for an unknown name.
 */
export function gameIconSvg(name: string, px: number): string | null {
  const draw = ICONS[name];
  if (!draw) return null;
  const used = new Set<Tone>();
  const kit: Kit = {
    f: (t) => {
      used.add(t);
      return `url(#t${t})`;
    },
    e: (t) => TONES[t][2],
    uid: (n) => `u${n}`,
  };
  const body = toSvg(draw(kit));
  const defs = [...used].map((t) => `<linearGradient id="t${t}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${TONES[t][0]}"/><stop offset="1" stop-color="${TONES[t][1]}"/></linearGradient>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 32 32"><defs>${defs}</defs>${body}</svg>`;
}

const NAME_PREFIX = /^(\p{Extended_Pictographic}\uFE0F?)\s+(.*)$/su;

/** A player name; a leading emoji the server gives bots and the boss ("🤖 Cobra") shows as its drawn icon. */
export function PlayerName({ name }: { name: string }) {
  const m = NAME_PREFIX.exec(name);
  if (!m || !iconForEmoji(m[1]!)) return <>{name}</>;
  return (
    <>
      <Glyph e={m[1]!} size="1.1em" /> {m[2]}
    </>
  );
}
